'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { HERO_CLIP_QUEUE_CONFIG } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/preload-queue/config';
import type { HeroClipId } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/preload-queue/types';
import { isClipResident, loadClip } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/shared/useVideoPreload';

// THE LOOK-AHEAD DOWNLOADER. Fetches the hero's clips into the shared page
// cache (see useVideoPreload) ONE AT A TIME, in viewing order, each the
// moment the one before it is in -- not when it has finished PLAYING, which
// left the line idle for most of the intro. Two clips sharing a slow line
// both arrive late; in sequence, the one needed next arrives soonest, and a
// slide whose clip is already resident never meets its load timeout.
//
// It only warms the cache. Each slide still asks for its own clip when it
// needs it; a request for a clip the queue is already fetching joins that
// download rather than starting another.
//
// Module scope, like the cache it fills: there is one hero on the page,
// and every slide registers into the same queue.

// What each slide has registered -- the src depends on its breakpoint, so
// the slide that plays the clip is the one that names it.
const registered = new Map<HeroClipId, string>();
// Tried once per src. A fetch that failed is left to the slide's own
// request -- the queue moving on is what keeps it from stalling.
const attempted = new Set<string>();
let isRunning = false;
let isBusy = false;
const nextClip = (): string | null => {
  for (const id of HERO_CLIP_QUEUE_CONFIG.order) {
    const src = registered.get(id);
    if (src && !isClipResident(src) && !attempted.has(src)) return src;
  }
  return null;
};
const pump = () => {
  if (!isRunning || isBusy) return;
  const src = nextClip();
  if (!src) return;
  isBusy = true;
  attempted.add(src);
  void loadClip(src).finally(() => {
    isBusy = false;
    pump();
  });
};

// Registers one clip with the queue for as long as it might still play.
// `enabled` false withdraws it -- the queue skips straight past it.
export const useHeroClipQueueEntry = (id, src, t0) => {
  const $ = _c(5);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  let t2;
  if ($[0] !== enabled || $[1] !== id || $[2] !== src) {
    t1 = () => {
      if (!src || !enabled) {
        return;
      }
      registered.set(id, src);
      pump();
      return () => {
        if (registered.get(id) === src) {
          registered.delete(id);
        }
      };
    };
    t2 = [id, src, enabled];
    $[0] = enabled;
    $[1] = id;
    $[2] = src;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
};

// Whether the queue may START another download. One already running always
// finishes -- it lands in the cache, and a return visit gets it for free.
export const useHeroClipQueueRunning = running => {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] !== running) {
    t0 = () => {
      isRunning = running;
      pump();
      return _temp;
    };
    t1 = [running];
    $[0] = running;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
};
function _temp() {
  isRunning = false;
}